import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  FaFacebookF,
  FaInstagram,
  FaEnvelope,
  FaArrowUp,
  FaMapMarkerAlt,
  FaPhoneAlt,
} from "react-icons/fa";

export default function Footer() {
  const [showScroll, setShowScroll] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowScroll(window.scrollY > 200);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const linkStyle =
    "text-white/80 hover:text-white transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30 rounded-sm";

  return (
    <footer className="bg-[#001b35] text-white text-sm relative">
      {/* Brand cap */}
      <div className="bg-gradient-to-b from-[#002b5c] to-[#001b35] text-white text-center px-6 md:px-8 pt-16 pb-12 border-b border-white/10">
        <h2 className="text-2xl font-extrabold tracking-tight">Your Choice Associates</h2>
        <p className="text-white/80 max-w-2xl mx-auto text-base mt-2">
          Empowering families with tailored insurance solutions
        </p>

        {/* Socials */}
        <div className="mt-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-white/80">Connect with us</p>
          <div className="mt-3 flex justify-center gap-3">
            <a
              href="https://www.facebook.com/profile.php?id=61591272397232"
              aria-label="Facebook"
              className="h-10 w-10 inline-flex items-center justify-center rounded-full bg-white/5 text-white hover:bg-white/10 ring-1 ring-white/10 transition"
            >
              <FaFacebookF />
            </a>
            <a
              href="https://www.instagram.com/yourchoiceassociates/"
              aria-label="Instagram"
              className="h-10 w-10 inline-flex items-center justify-center rounded-full bg-white/5 text-white hover:bg-white/10 ring-1 ring-white/10 transition"
            >
              <FaInstagram />
            </a>
          </div>
        </div>
      </div>

      {/* Links */}
      <div className="max-w-6xl mx-auto px-6 md:px-8 py-14 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-10">
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-white/70 mb-3">About</h4>
          <ul className="space-y-2">
            <li><Link to="/about" className={linkStyle}>Company Overview</Link></li>
            <li><Link to="/contact" className={linkStyle}>Support</Link></li>
            <li><Link to="/privacy" className={linkStyle}>Privacy Policy</Link></li>
            <li><Link to="/terms" className={linkStyle}>Terms &amp; Conditions</Link></li>
            <li><Link to="/sms-terms" className={linkStyle}>SMS Terms</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-white/70 mb-3">Medicare</h4>
          <ul className="space-y-2">
            <li><Link to="/medicare-a" className={linkStyle}>Medicare Advantage</Link></li>
            <li><Link to="/medicare-supplements" className={linkStyle}>Medicare Supplements</Link></li>
            <li><Link to="/medicare-d" className={linkStyle}>Part D Drug Plans</Link></li>
            <li><Link to="/help" className={linkStyle}>Help Center</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-white/70 mb-3">Life Insurance</h4>
          <ul className="space-y-2">
            <li><Link to="/term-life" className={linkStyle}>Term Life</Link></li>
            <li><Link to="/permanent-life" className={linkStyle}>Permanent Life</Link></li>
            <li><Link to="/contact" className={linkStyle}>Speak with an Agent</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-white/70 mb-3">Corporate Office</h4>
          <ul className="space-y-3 text-white/90">
            <li className="flex items-start gap-3">
              <FaMapMarkerAlt className="mt-1 text-[#e31c3d]" />
              <span>
                76 Mamaroneck Avenue Suite 22<br />
                White Plains, NY 10601
              </span>
            </li>
            <li className="flex items-center gap-3">
              <FaEnvelope className="text-[#e31c3d]" />
              <a
                href="mailto:support@yourchoiceassociates.org"
                className="hover:text-white transition focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30 rounded-sm"
              >
                support@yourchoiceassociates.org
              </a>
            </li>
            <li className="flex items-center gap-3">
              <FaPhoneAlt className="text-[#e31c3d]" />
              <a
                href="tel:+19143584068"
                className="hover:text-white transition focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30 rounded-sm"
              >
                +1 (914) 358-4068
              </a>
            </li>
          </ul>

          <div className="mt-6">
            <Link
              to="/quote"
              className="inline-flex items-center justify-center gap-2 bg-[#e31c3d] hover:bg-[#c01730] text-white font-semibold px-5 py-2.5 rounded-full transition shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
            >
              Get a Free Quote
            </Link>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="pb-6 border-t border-white/10">
        <div className="max-w-6xl pt-4 mx-auto px-6 md:px-8 text-center">
          <p className="text-xs font-semibold text-white">
            © {new Date().getFullYear()} Your Choice Associates. All rights reserved.
          </p>
          <p className="text-[11px] text-white/70">
            Powered by{" "}
            <a
              href="https://getihdtech.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-white hover:text-[#e31c3d] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30 rounded-sm"
            >
              IHD Agency
            </a>
          </p>
        </div>
      </div>

      {/* Scroll to top */}
      {showScroll && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 bg-[#e31c3d] hover:bg-[#c01730] text-white p-3 rounded-full shadow-lg transition
                     ring-1 ring-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
          aria-label="Scroll to top"
        >
          <FaArrowUp />
        </button>
      )}
    </footer>
  );
}
